import { useEffect, useState } from "react";
import { CheckCircle2, MessageSquare, Send, Loader2, Lightbulb, AlertTriangle } from "lucide-react";
import { curSym } from "@/lib/company";

const BASE = process.env.REACT_APP_BACKEND_URL;
const tl = (v) => `${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ${curSym()}`;

export default function FeedbackPublic() {
  const params = new URLSearchParams(window.location.search);
  const fis = params.get("fis") || "";
  const t = params.get("t") || "";
  const [ctx, setCtx] = useState(null);
  const [type, setType] = useState("suggestion");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!fis) return;
    fetch(`${BASE}/api/feedback/context?fis=${encodeURIComponent(fis)}&t=${encodeURIComponent(t)}`)
      .then((r) => r.json())
      .then((d) => { setCtx(d); if (d.customer_name) setName(d.customer_name); })
      .catch(() => {});
  }, [fis, t]);

  const submit = async () => {
    if (message.trim().length < 3) { setErr("Lütfen mesajınızı yazın."); return; }
    setSending(true); setErr("");
    try {
      const r = await fetch(`${BASE}/api/feedback`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sale_no: fis || null, t: t || null, type, message: message.trim(), name: name.trim() || null, phone: phone.trim() || null }),
      });
      if (!r.ok) throw new Error();
      setDone(true);
    } catch (e) { setErr("Gönderilemedi, lütfen tekrar deneyin."); }
    finally { setSending(false); }
  };

  if (done) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div data-testid="feedback-thankyou" className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-100">
        <CheckCircle2 className="mx-auto h-16 w-16 text-brand-green" />
        <h1 className="mt-4 font-heading text-2xl font-black text-slate-800">Teşekkürler!</h1>
        <p className="mt-2 text-slate-500">Geri bildiriminiz yöneticilerimize iletildi. En kısa sürede değerlendirilecek.</p>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100" data-testid="feedback-public-page">
        <div className="mb-1 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-green"><MessageSquare className="h-5 w-5 text-white" /></div>
          <p className="font-heading text-xl font-black">Groot <span className="text-brand-orange">ONE</span></p>
        </div>
        <h1 className="mt-3 font-heading text-lg font-bold text-slate-800">Öneri / Şikayet Formu</h1>
        <p className="text-sm text-slate-500">Bizi tercih ettiğiniz için teşekkürler. Bir öneriniz, şikayetiniz veya bir yanlışlık varsa bize iletin.</p>
        {ctx?.sale_no && (
          <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm ring-1 ring-inset ring-slate-100">
            <p className="text-slate-500">Fiş No: <b className="font-mono text-slate-700">{ctx.sale_no}</b></p>
            {ctx.total != null && <p className="text-slate-500">Alışveriş Tutarı: <b className="text-brand-green">{tl(ctx.total)}</b></p>}
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button data-testid="feedback-type-suggestion" onClick={() => setType("suggestion")} className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ring-1 ring-inset ${type === "suggestion" ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200"}`}><Lightbulb className="h-4 w-4" /> Öneri</button>
          <button data-testid="feedback-type-complaint" onClick={() => setType("complaint")} className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ring-1 ring-inset ${type === "complaint" ? "bg-red-500 text-white ring-red-500" : "bg-white text-slate-600 ring-slate-200"}`}><AlertTriangle className="h-4 w-4" /> Şikayet</button>
        </div>
        <textarea data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Mesajınız..." className="mt-3 w-full rounded-md border border-input p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" />
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input data-testid="feedback-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Adınız (opsiyonel)" className="h-10 rounded-md border border-input px-3 text-sm" />
          <input data-testid="feedback-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefon (opsiyonel)" className="h-10 rounded-md border border-input px-3 text-sm" />
        </div>
        {err && <p data-testid="feedback-error" className="mt-2 text-sm text-red-500">{err}</p>}
        <button data-testid="feedback-submit" onClick={submit} disabled={sending} className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-brand-green py-3 font-semibold text-white hover:bg-brand-green-600 disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Yolla
        </button>
      </div>
    </div>
  );
}
